import LayoutNested from "@/components/layouts/LayoutNested.vue";

/**
 * Route Factory
 *
 * Generates a Vue Router route definition for an event module config.
 * All routes point to shared views under @/services/hrdc_event/views/.
 *
 * @param {object} config - Event module config from eventRegistry
 * @returns {object} Vue Router route definition
 */
export function createRoutes(config) {
  const s = config.pathSuffix; // e.g. "einvoicing"
  const r = config.routePrefix; // e.g. "HrdcEinvoicing"
  const m = config.modelKeyPrefix; // e.g. "hrdcEinvoicing"
  const M = config.modelKeyPrefix.charAt(0).toUpperCase() + config.modelKeyPrefix.slice(1); // e.g. "HrdcEinvoicing"

  // Helper for route props with backTo
  const propsWithBackTo = (modelKey) => (route) => ({
    params: {
      modelKey,
      backTo: route.params.backTo,
    },
  });

  // Helper for static props
  const staticProps = (modelKey) => ({
    params: { modelKey },
  });

  return {
    path: `/service/:serviceKey`,
    component: LayoutNested,
    children: [
      {
        path: `application/view_${s}`,
        name: `${r}ViewApplication`,
        meta: { breadcrumb: ["Applications", "View"] },
        component: () =>
          import("@/services/hrdc_event/views/application/View.vue"),
        props: propsWithBackTo(`${m}Application`),
      },
      {
        path: `application/edit_${s}`,
        name: `${r}EditApplication`,
        meta: { breadcrumb: ["Applications", "Edit"] },
        component: () =>
          import("@/services/hrdc_event/views/application/Edit.vue"),
        props: propsWithBackTo(`${m}Application`),
      },
      {
        path: `application/add_${s}`,
        name: `${r}AddApplication`,
        meta: { breadcrumb: ["Applications", "Add"] },
        component: () =>
          import("@/services/hrdc_event/views/application/Add.vue"),
        props: propsWithBackTo(`${m}Application`),
      },
      {
        path: `application_${s}`,
        name: `${r}Application`,
        component: () =>
          import("@/services/hrdc_event/views/application/Index.vue"),
        props: staticProps(`${m}Application`),
      },
      {
        path: `application/email/client_${s}`,
        name: `${r}SendEmailToClient`,
        meta: { breadcrumb: ["Applications", "Email to Client"] },
        component: () =>
          import("@/services/hrdc_event/views/email_client/Add.vue"),
        props: propsWithBackTo(`${m}EmailClient`),
      },
      {
        path: `application/revert_form_${s}`,
        name: `${r}ClientRevertForm`,
        meta: { breadcrumb: ["Applications", "Revert Form"] },
        component: () =>
          import("@/services/hrdc_event/views/revert/Add.vue"),
        props: propsWithBackTo(`${m}RevertForm`),
      },
      {
        path: `application/manual_invoice_${s}`,
        name: `${r}ManualInvoiceToHrdc`,
        meta: { breadcrumb: ["Applications", "Manual Invoice"] },
        component: () =>
          import("@/services/hrdc_event/views/manual_invoice/Add.vue"),
        props: propsWithBackTo(`${m}ManualInvoice`),
      },
      {
        path: `application/claim_submit_${s}`,
        name: `${r}ClaimSubmitToHrdc`,
        meta: { breadcrumb: ["Applications", "Claim Submission"] },
        component: () =>
          import("@/services/hrdc_event/views/claim_submit/Add.vue"),
        props: propsWithBackTo(`${m}ClaimSubmit`),
      },
      {
        path: `application/claim_approved_${s}`,
        name: `${r}ClaimApprovedFromHrdc`,
        meta: { breadcrumb: ["Applications", "Claim Approved"] },
        component: () =>
          import("@/services/hrdc_event/views/claim_approved/Add.vue"),
        props: propsWithBackTo(`${m}ClaimApproved`),
      },
      {
        path: `application/payment_received_${s}`,
        name: `${r}PaymentReceivedFromHrdc`,
        meta: { breadcrumb: ["Applications", "Payment Received"] },
        component: () =>
          import("@/services/hrdc_event/views/payment_received/Add.vue"),
        props: propsWithBackTo(`${m}PaymentReceived`),
      },
      {
        path: `application/refund_email_submission_${s}`,
        name: `${r}RefundEmailSubmission`,
        meta: { breadcrumb: ["Applications", "Refund Email Submission"] },
        component: () =>
          import(
            "@/services/hrdc_event/views/refund_email_submission/Add.vue"
          ),
        props: propsWithBackTo(`${m}RefundEmailSubmission`),
      },
      {
        path: `application/refund_email_approval_1_${s}`,
        name: `${r}RefundEmailApproval1`,
        meta: { breadcrumb: ["Applications", "Refund Approval 1"] },
        component: () =>
          import(
            "@/services/hrdc_event/views/refund_email_approval_1/Add.vue"
          ),
        props: propsWithBackTo(`${m}RefundEmailApproval1`),
      },
      {
        path: `application/refund_email_approval_2_${s}`,
        name: `${r}RefundEmailApproval2`,
        meta: { breadcrumb: ["Applications", "Refund Approval 2"] },
        component: () =>
          import(
            "@/services/hrdc_event/views/refund_email_approval_2/Add.vue"
          ),
        props: propsWithBackTo(`${m}RefundEmailApproval2`),
      },
      {
        path: `application/refund_email_ar_to_ap_${s}`,
        name: `${r}RefundEmailArToAp`,
        meta: { breadcrumb: ["Applications", "Refund AR to AP"] },
        component: () =>
          import(
            "@/services/hrdc_event/views/refund_email_ar_to_ap/Add.vue"
          ),
        props: propsWithBackTo(`${m}RefundEmailArToAp`),
      },
      {
        path: `refund_ap_to_cimb_${s}`,
        name: `${r}RefundEmailApToCimb`,
        component: () =>
          import(
            "@/services/hrdc_event/views/finance_ap_submit_refund_to_cimb/Index.vue"
          ),
        props: propsWithBackTo(`${m}RefundEmailApToCimb`),
      },
      {
        path: `application/email_payment_advice_to_client_${s}`,
        name: `${r}EmailPaymentAdviceToClient`,
        meta: { breadcrumb: ["Applications", "Payment Advice to Client"] },
        component: () =>
          import(
            "@/services/hrdc_event/views/email_payment_advice_to_client/Add.vue"
          ),
        props: propsWithBackTo(`${m}EmailPaymentAdviceToClient`),
      },
      {
        path: `submission_date_${s}`,
        name: `${r}ApplicationSubmissionDate`,
        meta: { breadcrumb: ["Submission Date"] },
        component: () =>
          import(
            "@/services/hrdc_event/views/application/SubmissionDate.vue"
          ),
        props: staticProps(`${m}ApplicationSubmissionDate`),
      },
      {
        path: `report_analysis_${s}`,
        name: `${r}ReportAnalysis`,
        component: () =>
          import("@/services/hrdc_event/views/report/ReportAnalysis.vue"),
        props: staticProps(`${M}ReportAnalysis`),
      },
      {
        path: `event_settings_report_analysis_${s}`,
        name: `${r}EventSettingsReportAnalysis`,
        meta: { breadcrumb: ["Report Analysis"] },
        component: () =>
          import(
            "@/services/hrdc_event/views/report/EventSettingsReportAnalysis.vue"
          ),
        props: staticProps(`${M}ReportAnalysis`),
      },
      {
        path: `general_info_${s}`,
        name: `${r}AddGeneralInfo`,
        meta: { breadcrumb: ["Applications", "General Info"] },
        component: () =>
          import("@/services/hrdc_event/views/general_info/Add.vue"),
        props: propsWithBackTo(`${m}GeneralInfo`),
      },
      {
        path: `settings_${s}`,
        name: `${r}Settings`,
        component: () =>
          import("@/services/hrdc_event/views/settings/Index.vue"),
        props: staticProps(`${M}SettingsPrice`),
      },
      {
        path: `settings/price_${s}`,
        name: `${r}SettingsPrice`,
        meta: { breadcrumb: ["Settings", "Price"] },
        component: () =>
          import("@/services/hrdc_event/views/settings/price/Index.vue"),
        props: staticProps(`${M}SettingsPrice`),
      },
      {
        path: `settings/error_log_${s}`,
        name: `${r}SettingsErrorLog`,
        meta: { breadcrumb: ["Settings", "Error Log"] },
        component: () =>
          import(
            "@/services/hrdc_event/views/settings/error_log/Index.vue"
          ),
        props: staticProps(`${M}SettingsErrorLog`),
      },
      {
        path: `settings/events_${s}`,
        name: `${r}SettingsEvents`,
        meta: { breadcrumb: ["Settings", "Events"] },
        component: () =>
          import("@/services/hrdc_event/views/settings/event/Index.vue"),
        props: staticProps(`${M}SettingsEvents`),
      },
      // settings/xero_token moved to the Developer Console (/en/developer).
      {
        path: `settings/attachment_${s}`,
        name: `${r}SettingsAttachment`,
        meta: { breadcrumb: ["Settings", "Attachment"] },
        component: () =>
          import(
            "@/services/hrdc_event/views/settings/attachment/Index.vue"
          ),
        props: staticProps(`${M}SettingsAttachment`),
      },
      {
        path: `${config.xeroCallbackSuffix}`,
        component: () =>
          import("@/services/hrdc_event/views/callback/Index.vue"),
        props: staticProps(`${m}Application`),
      },
      {
        path: `callback_xero_${s}`,
        component: () =>
          import("@/services/hrdc_event/views/callback/xero/Index.vue"),
        props: staticProps(`${m}Application`),
      },
    ],
  };
}
